import React from "react";
import clsx from "clsx";
import { useNavigate } from "react-router-dom";

import { getTimeFromDateString } from "../../../libs/dateFormat";

import { REPORT_STATUS } from "../../../services/mangerReportServices";
import { HistoryItem } from "../../../services/historyServices";

import VerticalRule from "../../Rule/Vertical";
import { ReportStatusRadioIcon } from "../../../pages/Manager/Report";
import HorizontalRule from "../../Rule/Horizontal";
import ProgressBar from "../../ProgressBar";
import { CancelCompleteBadge } from "../IncommodityItem";

type Props = { index: number; item: HistoryItem; items: HistoryItem[] };

export default function HistoryServiceItem({ index, item, items }: Props) {
  const navigate = useNavigate();

  const getServiceStatus = (status: string) => {
    switch (status) {
      case "REQUESTED":
        return "REQUESTED";
      case "COMPLETED":
        return "COMPLETED";
      case "CANCELED":
        return "CANCELED";
      default:
        return "DEFAULT";
    }
  };

  const getServiceStatusStep = (status: string) => {
    switch (status) {
      case "REQUESTED":
        return 1;
      case "COMPLETED":
        return 2;
      case "CANCELED":
        return 0;
      default:
        return 0;
    }
  };

  return (
    <React.Fragment key={index}>
      <div className="flex gap-2.5 relative">
        <div className="h-full w-[24px] flex justify-center absolute z-0">
          {items.length > 1 && (
            <VerticalRule
              className={clsx([
                "!h-full !bg-[#d9d9d9]",
                {
                  "!h-[calc(100%-24px)] ": index === 0,
                  "!h-[24px]": index === items.length - 1,
                },
              ])}
              wrapperClassName={clsx([
                {
                  "!items-end": index === 0,
                  "!items-start": index === items.length - 1,
                },
              ])}
            />
          )}
        </div>
        <div className="w-[24px] pt-[18px] z-10">
          <ReportStatusRadioIcon status={getServiceStatus(item.status)} />
        </div>
        <div className="flex flex-col gap-1 pt-[18px]">
          <div className="text-base font-bold">
            {getTimeFromDateString(item.createdAt)}
          </div>
          <div className=" text-xs text-[#9f9f9f]">
            {item.createdAt.split(" ")[0]}
          </div>
          {item.status === REPORT_STATUS.CANCELED.toString() && (
            <CancelCompleteBadge />
          )}
        </div>
        <div
          className={clsx([
            "p-[15px] border border-brand-neutral-10 rounded-[10px] flex flex-col gap-2.5 flex-1 h-fit",
            {
              "bg-brand-neutral-05":
                item.status === REPORT_STATUS.CANCELED.toString(),
            },
          ])}
          onClick={() => navigate(`/history/${item.type}/${item.serviceId}`)}
        >
          <div className="flex gap-2.5">
            <HistoryServiceItemIcon />
            <div className="flex flex-col gap-0.5">
              <div
                className={clsx([
                  {
                    " text-brand-neutral-50":
                      item.status === REPORT_STATUS.CANCELED.toString(),
                  },
                ])}
              >
                {item.lowerCategoryName}
              </div>
              <div className="text-xs text-brand-neutral-50">
                {item.midCategoryName}
              </div>
            </div>
          </div>
          {item.status !== REPORT_STATUS.CANCELED.toString() && (
            <div className="flex flex-col gap-2.5">
              <HorizontalRule margin="0" />
              <div className="flex flex-col gap-1.5">
                <div className=" bg-brand-primary-magenta bg-clip-text text-transparent font-semibold w-fit text-xs">
                  {item?.statusName}
                </div>
                <ProgressBar
                  currentStep={getServiceStatusStep(item?.status)}
                  wrapperClassName="!p-0"
                  className="h-2.5 !bg-brand-primary-magenta"
                  rounded
                  nonSticky
                  totalStep={2}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      {index < items.length - 1 && (
        <div className="h-5 w-[24px] flex justify-center">
          <VerticalRule className="!h-full !bg-[#d9d9d9]" />
        </div>
      )}
    </React.Fragment>
  );
}
export const HistoryServiceItemIcon = () => (
  <svg
    width="40"
    height="40"
    viewBox="0 0 40 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="0.5"
      y="0.5"
      width="39"
      height="39"
      rx="3.5"
      fill="white"
      stroke="#CFCFCF"
    />
    <path
      d="M15.131 30.9953L7.59656 32.368L8.96925 24.8379"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M15.131 30.9948L32.055 14.0664C32.4102 13.7111 32.4102 13.1279 32.055 12.7726L27.1914 7.909C26.8362 7.55377 26.2529 7.55377 25.8976 7.909L8.96924 24.8374L15.1266 30.9948H15.131Z"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M24.2048 9.60156L30.3622 15.7633"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M25.299 16.4453L18.1487 23.5919L14.1989 27.5454"
      stroke="url(#paint0_linear_1398_38673)"
      strokeWidth="2"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
    <path
      d="M8.17972 29.2627L10.7014 31.7844"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13.6925 20.1491L6.8773 13.3339C6.70626 13.1629 6.70626 12.8866 6.8773 12.7155L12.4602 7.12828C12.6312 6.95724 12.9075 6.95724 13.0785 7.12828L19.8236 13.8733"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M26.0731 20.0703L32.6207 26.6662C32.7918 26.8373 32.7918 27.1136 32.6207 27.2846L27.0335 32.8719C26.8625 33.0429 26.5862 33.0429 26.4151 32.8719L19.8236 26.3724"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10.5612 12.4656L14.4907 8.53613"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
    <path
      d="M13.9732 12.772L16.3502 10.3994"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
    <path
      d="M15.8327 14.6368L18.2097 12.2598"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
    <path
      d="M27.1344 29.0389L31.0639 25.1094"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
    <path
      d="M24.7004 23.9385L27.073 21.5615"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
    <path
      d="M26.5598 25.7974L28.9368 23.4248"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
    <defs>
      <linearGradient
        id="paint0_linear_1398_38673"
        x1="25.299"
        y1="16.4453"
        x2="13.4811"
        y2="19.4869"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#E100A3" />
        <stop offset="1" stopColor="#EA27C2" />
      </linearGradient>
    </defs>
  </svg>
);
